import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import toastUtils from '../../utils/toastUtils';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import {
  Button,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tooltip
} from '@material-ui/core';
import {
  FilterList as FilterIcon,
  Settings as SettingsIcon,
  PlaylistAddCheck as BulkIcon,
  AutorenewRounded as AutomationIcon,
  AssessmentOutlined,
  SettingsInputComponent,
  Search as SearchIcon,
  Update as RealTimeIcon
} from '@material-ui/icons';
import { format } from 'date-fns';
import { Can } from '../../components/Can';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import KanbanBoard from './KanbanBoard';
import useKanbanTasks from '../../hooks/useKanbanTasks';
import useKanbanAutomations from '../../hooks/useKanbanAutomations';
import useKanbanWebSocket from '../../hooks/useKanbanWebSocket';
import KanbanFilters from './KanbanFilters';
import KanbanAutomationModal from './KanbanAutomationModal';
import KanbanAnalytics from './KanbanAnalytics';
import KanbanBulkActions from './KanbanBulkActions';
import KanbanTaskModal from './KanbanTaskModal';

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    display: 'flex',
    padding: theme.spacing(1),
    overflowX: 'auto',
    ...theme.scrollbarStyles,
    borderRadius: '10px',
  },
  button: {
    borderRadius: '10px',
    whiteSpace: 'nowrap',
  },
  dateInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    marginRight: theme.spacing(1),
    minWidth: '140px',
    [theme.breakpoints.down('sm')]: {
      minWidth: '120px',
    },
  },
  sortSelect: {
    minWidth: 150,
    marginRight: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '120px',
    },
  },
  headerButtonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    overflowX: 'auto',
    paddingBottom: theme.spacing(0.5),
    '&::-webkit-scrollbar': {
      height: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '2px',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const queueIds = user && user.queues ? user.queues.map(queue => queue.UserQueue.queueId) : [];

  // Enhanced Kanban state
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [showAutomations, setShowAutomations] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [realTimeUpdates, setRealTimeUpdates] = useState(true);
  const [viewMode, setViewMode] = useState('board'); // 'board', 'list', 'calendar'

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('sortOrder') || 'ticketNumber';
  });

  // Use enhanced hooks
  const kanbanTasks = useKanbanTasks();
  const kanbanAutomations = useKanbanAutomations();
  
  // WebSocket integration for real-time updates
  const { emitTaskView, emitTaskEdit, emitBulkOperation } = useKanbanWebSocket({
    onTaskUpdate: (data) => {
      if (data.action === 'task-updated' || data.action === 'task-moved' || data.action === 'task-assigned') {
        fetchTickets();
      }
    },
    onAutomationUpdate: (data) => {
      if (data.action === 'automation-triggered') {
        fetchTickets();
      }
    },
    onCollaborationUpdate: (data) => {
      // Handle collaboration updates (show user indicators, etc.)
      console.log('Collaboration update:', data);
    },
    enabled: realTimeUpdates
  });

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTags = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets(fetchedTags);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTickets = async (fetchedTags = tags) => {
    try {
      const { data } = await api.get('/ticket/kanban', {
        params: {
          queueIds: JSON.stringify(queueIds),
          startDate: startDate,
          endDate: endDate,
        },
      });
      setTickets(data.tickets);
      organizeLanes(fetchedTags, data.tickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;

    const onAppMessage = data => {
      if (['create', 'update', 'delete'].includes(data.action)) {
        fetchTickets();
      }
    };

    const onKanbanUpdate = data => {
      if (realTimeUpdates) {
        if (data.action === 'task-moved') {
          toastUtils.info(i18n.t('kanban.messages.taskMovedByUser', { user: data.user?.name }), 'Card Movido');
          fetchTickets();
        } else if (data.action === 'task-assigned') {
          toastUtils.info(i18n.t('kanban.messages.taskAssignedByUser', { user: data.user?.name }), 'Card Criado');
          fetchTickets();
        }
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);
    socket.on(`company-${companyId}-kanban-update`, onKanbanUpdate);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
      socket.off(`company-${companyId}-kanban-update`, onKanbanUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId, realTimeUpdates]);

  const handleSearchClick = () => {
    fetchTickets();
  };

  const handleStartDateChange = event => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = event => {
    setEndDate(event.target.value);
  };

  const updateTicket = updatedTicket => {
    setTickets(prevTickets =>
      prevTickets.map(ticket =>
        ticket.id === updatedTicket.id ? updatedTicket : ticket
      )
    );
  };

  const getOpportunityValue = (ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return opportunityValue;
  };

  const organizeLanes = (fetchedTags = tags, fetchedTickets = tickets) => {
    const sortedTickets = [...fetchedTickets];

    if (sortOrder === 'ticketNumber') {
      sortedTickets.sort((a, b) => a.id - b.id);
    } else if (sortOrder === 'lastMessageTime') {
      sortedTickets.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    } else if (sortOrder === 'valorDesc') {
      sortedTickets.sort((a, b) => {
        const valorA = getOpportunityValue(a);
        const valorB = getOpportunityValue(b);
        return valorB - valorA;
      });
    }

    const defaultTickets = sortedTickets.filter(
      ticket => ticket.tags.length === 0
    );

    const lanesData = [
      {
        id: 'lane0',
        title: i18n.t('tagsKanban.laneDefault'),
        tickets: defaultTickets,
        color: '#757575',
      },
      ...fetchedTags.map(tag => {
        const taggedTickets = sortedTickets.filter(ticket =>
          ticket.tags.some(t => t.id === tag.id)
        );
        return {
          id: tag.id.toString(),
          title: tag.name,
          tickets: taggedTickets,
          color: tag.color || '#757575',
        };
      }),
    ];

    setLanes(lanesData);
  };

  useEffect(() => {
    organizeLanes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, tickets, sortOrder]);

  const handleCardMove = async (ticketId, targetLaneId) => {
    ticketId = parseInt(ticketId, 10);
    try {
      // Emit real-time update
      emitTaskEdit(ticketId);
      
      await api.delete(`/ticket-tags/${ticketId}`);

      if (targetLaneId !== 'lane0') {
        await api.put(`/ticket-tags/${ticketId}/${targetLaneId}`);
        toastUtils.success(i18n.t('kanban.messages.taskUpdated'), 'Card Movido');
      } else {
        toastUtils.success(i18n.t('kanban.messages.taskUpdated'), 'Card Movido');
      }

      fetchTickets();
    } catch (err) {
      console.log(err);
      toastUtils.error(i18n.t('kanban.messages.errorMovingTask'), 'Erro no Kanban');
    }
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleTaskSelection = (taskId, selected) => {
    setSelectedTasks(prev => 
      selected 
        ? [...prev, taskId]
        : prev.filter(id => id !== taskId)
    );
  };

  const handleSelectAllTasks = () => {
    const allTaskIds = lanes.flatMap(lane => lane.tickets.map(ticket => ticket.id));
    setSelectedTasks(selectedTasks.length === allTaskIds.length ? [] : allTaskIds);
  };

  const handleBulkAction = async (action, data) => {
    try {
      // Emit WebSocket event for real-time collaboration
      emitBulkOperation(action, selectedTasks);
      
      await kanbanTasks.bulkUpdate(selectedTasks, { action, ...data });
      setSelectedTasks([]);
      fetchTickets();
    } catch (error) {
      console.error('Bulk action failed:', error);
    }
  };

  const handleTaskCreated = (newTask) => {
    // Refresh tickets to show the new task
    fetchTickets();
  };

  const handleAddColumnClick = () => {
    history.push('/tagsKanban');
  };

  const applyFilters = (newFilters) => {
    setFilters(newFilters);
    fetchTickets();
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 0:
        return (
          <KanbanBoard
            lanes={lanes}
            onCardMove={handleCardMove}
            updateTicket={updateTicket}
            selectedTasks={selectedTasks}
            onTaskSelection={handleTaskSelection}
            viewMode={viewMode}
            onTaskCreated={handleTaskCreated}
            onLaneUpdated={fetchTags}
          />
        );
      case 1:
        return (
          <KanbanAnalytics 
            tasks={lanes.flatMap(lane => lane.tickets)}
            lanes={lanes}
          />
        );
      case 2:
        return (
          <div style={{ padding: 16 }}>
            <Typography variant="h6" gutterBottom>
              {i18n.t('kanban.automation.title')}
            </Typography>
            <Typography color="textSecondary">
              {i18n.t('kanban.automation.description')}
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => setShowAutomations(true)}
              style={{ marginTop: 16 }}
            >
              {i18n.t('kanban.automation.manage')}
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <MainHeaderButtonsWrapper className={classes.headerButtonsWrapper}>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.sortSelect}
          >
            <InputLabel htmlFor="sort-order-select">{i18n.t('kanban.filters.sortBy')}</InputLabel>
            <Select
              native
              value={sortOrder}
              onChange={handleSortOrderChange}
              label={i18n.t('kanban.filters.sortBy')}
              inputProps={{
                name: 'sortOrder',
                id: 'sort-order-select',
              }}
            >
              <option value="ticketNumber">{i18n.t('kanban.filters.ticketNumber')}</option>
              <option value="lastMessageTime">{i18n.t('kanban.filters.lastMessage')}</option>
              <option value="valorDesc">{i18n.t('kanban.filters.valueDesc')}</option>
              <option value="priority">{i18n.t('kanban.filters.priority')}</option>
              <option value="dueDate">{i18n.t('kanban.filters.dueDate')}</option>
            </Select>
          </FormControl>
          <TextField
            label={i18n.t('kanban.filters.startDate')}
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
          />
          <TextField
            label={i18n.t('kanban.filters.endDate')}
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
          />
          <Tooltip title={i18n.t('kanban.filters.search')}>
            <IconButton
              onClick={handleSearchClick}
              size="small"
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={i18n.t('kanban.filters.advancedFilters')}>
            <IconButton onClick={() => setShowFilters(true)} size="small">
              <FilterIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={i18n.t('kanban.automation.title')}>
            <IconButton onClick={() => setShowAutomations(true)} size="small">
              <SettingsInputComponent />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={i18n.t('kanban.analytics.title')}>
            <IconButton onClick={() => setCurrentTab(1)} size="small">
              <AssessmentOutlined />
            </IconButton>
          </Tooltip>
          
          {selectedTasks.length > 0 && (
            <Tooltip title={i18n.t('kanban.bulk.title')}>
              <IconButton onClick={() => setShowBulkActions(true)} size="small">
                <BulkIcon />
                <Chip 
                  size="small" 
                  label={selectedTasks.length} 
                  color="primary" 
                  style={{ marginLeft: 4 }}
                />
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title={i18n.t('kanban.realTimeUpdates')}>
            <IconButton
              onClick={() => setRealTimeUpdates(!realTimeUpdates)}
              size="small"
              color={realTimeUpdates ? 'primary' : 'default'}
            >
              <RealTimeIcon />
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      
      <Paper variant="outlined" style={{ marginBottom: 16 }}>
        <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
          <Tab label={i18n.t('kanban.tabs.board')} />
          <Tab label={i18n.t('kanban.tabs.analytics')} />
          <Tab label={i18n.t('kanban.tabs.automations')} />
        </Tabs>
      </Paper>
      
      <Paper variant="outlined" className={classes.mainPaper}>
        {renderTabContent()}
      </Paper>
      
      {/* Enhanced Modals */}
      <KanbanFilters 
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onApplyFilters={applyFilters}
      />
      
      <KanbanAutomationModal
        open={showAutomations}
        onClose={() => setShowAutomations(false)}
        automations={kanbanAutomations.automations}
      />
      
      {selectedTasks.length > 0 && (
        <>
          <KanbanBulkActions
            open={showBulkActions}
            onClose={() => setShowBulkActions(false)}
            selectedTasks={selectedTasks}
            onBulkAction={handleBulkAction}
          />
        </>
      )}
    </MainContainer>
  );
};

export default Kanban;
